import { useQuery } from '@tanstack/react-query'
import Autoplay from 'embla-carousel-autoplay'
import Link from 'next/link'
import * as React from 'react'

import { cn } from '@/lib/utils'
import useLocale from '@/hooks/use-locale'

import HallOfFameProjectCard from '@/components/cards/hall-of-fame-project-card'
import { PageActions, PageHeader, PageHeaderDescription, PageHeaderHeading } from '@/components/common/page-header'
import { Shell } from '@/components/common/shell'
import { Button } from '@/components/ui/button'
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel'

import hallOfFameProjectService from '@/services/hall-of-fame-project.service'

import { HallOfFameProject } from '@/types/hall-of-fame-project.type'
import { ListResponse } from '@/types/list.type'

function SectionHallOfFameHeading() {
  const { text } = useLocale()

  return (
    <Shell className="px-4 sm:px-6 md:px-8 py-8 relative overflow-hidden">
      <PageHeader className="space-y-4 sm:space-y-6 md:space-y-8 text-center w-full items-center justify-center flex mx-auto mt-12">
        <PageHeaderHeading
          as="h2"
          size="lg"
          className="tracking-normal text-3xl sm:text-4xl md:text-5xl lg:text-6xl/tight md:leading-tight text-center"
        >
          {text.HALL_OF_FAME_SECTION.TITLE}
        </PageHeaderHeading>
        <PageHeaderDescription className="text-center text-base sm:text-lg md:text-xl mx-auto">
          {text.HALL_OF_FAME_SECTION.DESCRIPTION}
        </PageHeaderDescription>
        <PageActions className="justify-center mt-6 sm:mt-8 md:mt-10">
          <Button className="border-primary text-primary hover:text-primary" size="lg" asChild variant="outline">
            <Link href="/hall-of-fame">{text.HALL_OF_FAME_SECTION.VIEW_ALL}</Link>
          </Button>
        </PageActions>
      </PageHeader>
    </Shell>
  )
}

function HallOfFameCarousel() {
  const page = 1
  const pageSize = 12
  const { locale } = useLocale()

  const plugin = React.useRef(Autoplay({ delay: 2000, stopOnInteraction: false }))

  const {
    data: projects,
    isLoading,
    isError,
  } = useQuery<ListResponse<HallOfFameProject>>({
    queryKey: ['hall-of-fame-carousel', page, pageSize, locale],
    queryFn: () => {
      return hallOfFameProjectService.getProjects({
        queryParams: {
          searchFields: ['course'],
          pageNumber: page,
          pageSize,
          sort: '-stars',
        },
        locale,
      })
    },
  })

  if (isLoading) {
    return (
      <div className="w-full py-12 flex justify-center items-center">
        <div className="animate-pulse h-96 w-full container bg-gray-100 rounded-md"></div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="w-full py-12 flex justify-center items-center">
        <p className="text-red-500">Failed to load projects</p>
      </div>
    )
  }

  if (!projects?.data.length) {
    return null
  }

  return (
    <div className="container">
      <Carousel
        className="w-full relative"
        plugins={[plugin.current]}
        onMouseEnter={plugin.current.stop}
        onMouseLeave={plugin.current.reset}
        opts={{
          loop: true,
        }}
      >
        <CarouselContent className="-ml-4 sm:-ml-6">
          {projects.data.map(project => (
            <CarouselItem key={project.id} className="pl-4 md:pl-6 md:basis-1/2 lg:basis-1/3">
              <HallOfFameProjectCard project={project} className="h-full" />
            </CarouselItem>
          ))}
        </CarouselContent>
        <div className="flex justify-center sm:justify-end mt-8 gap-4">
          <CarouselPrevious className="static translate-y-0 mx-0" />
          <CarouselNext className="static translate-y-0 mx-0" />
        </div>
      </Carousel>
    </div>
  )
}

function SectionHallOfFame({ className }: { className?: string }) {
  return (
    <div className={cn('mt-8', className)}>
      <SectionHallOfFameHeading />
      <HallOfFameCarousel />
    </div>
  )
}

export default SectionHallOfFame
